import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCar, FaClock, FaMapMarkerAlt, FaCheckCircle, FaCalendarAlt, FaStar, FaChevronRight, FaChevronLeft, FaCamera, FaUser, FaSignInAlt, FaCouch, FaPlus, FaMoneyBillWave, FaShieldAlt, FaSearch, FaCarSide, FaTruckMonster, FaArrowRight, FaBed, FaChair, FaTimes, FaInfoCircle } from 'react-icons/fa';
import pb from '../pocketbase';
import { notifyNewBooking } from '../services/notificationService';
import confetti from 'canvas-confetti';
import './ReservationPage.css';

// Import images for formulas
import luxe1 from '../assets/interieur-luxe/interieur-luxe1.jpg';
import luxe2 from '../assets/interieur-luxe/interieur-luxe2.jpg';
import luxe3 from '../assets/interieur-luxe/interieur-luxe3.jpg';

const BANNER_IMG = "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80";

const TIMES = [
    "09:00", "10:00", "11:00", "12:00",
    "14:00", "15:00", "16:00", "17:00", "18:00"
];

const CATEGORIES = [
    { id: 'Citadine', name: 'Citadines', icon: <FaCarSide />, label: 'Compactes' },
    { id: 'Berline', name: 'Berlines', icon: <FaCar />, label: 'SUV Compacts' },
    { id: 'SUV / Monospace', name: 'SUV & XL', icon: <FaTruckMonster />, label: 'Gros Volumes' }
];

const FORMULA_DETAILS = {
    "PROPRETÉ STANDARD": {
        subtitle: "Idéal pour un entretien régulier",
        features: ["Aspiration habitacle & coffre", "Nettoyage tableau de bord", "Nettoyage plastiques", "Vitrages intérieurs", "Désinfection vapeur", "Parfum d'ambiance"],
        img: luxe1
    },
    "PROPRETÉ PREMIUM": {
        subtitle: "Nettoyage en profondeur",
        featured: true,
        features: ["Pack Standard inclus", "Nettoyage sièges cuir/tissu", "Pressing tapis & moquettes", "Nettoyage des recoins", "Shampoing des plastiques", "Traitement anti-odeurs"],
        img: luxe2
    },
    "PROPRETÉ PRESTIGE": {
        subtitle: "Le soin ultime & exhaustif",
        features: ["Pack Premium inclus", "Protection plastiques UV", "Traitement complet cuirs", "Nettoyage ciel de toit", "Bords de portes & coffre", "Compartiment roue de secours"],
        img: luxe3
    }
};

const MOBILIER_CATEGORIES = [
    { id: 'canapes', name: 'Canapés', icon: <FaCouch />, label: 'Toutes tailles' },
    { id: 'matelas', name: 'Matelas', icon: <FaBed />, label: 'Sain & Propre' },
    { id: 'autres', name: 'Autres', icon: <FaChair />, label: 'Tapis & Chaises' }
];

const MOBILIER_DETAILS = {
    "CANAPÉ 2 PLACES": { subtitle: "Idéal pour petits espaces", features: ["Aspiration profondeur", "Extraction injections", "Détachage ciblé", "Traitement odeurs", "Désinfection vapeur"], img: "https://images.unsplash.com/photo-1550254478-ead40cc54513?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "CANAPÉ 3 PLACES": { subtitle: "Standard familial", featured: true, features: ["Pack Standard inclus", "Nettoyage accoudoirs", "Arrière des dossiers", "Désinfection textile", "Séchage accéléré"], img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "CANAPÉ D'ANGLE": { subtitle: "Entretien XL", features: ["Pack Premium inclus", "Nettoyage méridienne", "Traitement des recoins", "Shampouinage intégral", "Parfum textile luxe"], img: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "1 PLACE (90cm)": { subtitle: "Hygiène parfaite", features: ["Injection-Extraction", "Anti-acariens radical", "Élimination auréoles", "Désinfection vapeur", "Séchage naturel"], img: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "2 PLACES (140/160cm)": { subtitle: "Sommeil réparateur", featured: true, features: ["Nettoyage intégral", "Traitement bactérien", "Vapeur haute pression", "Extraction poussière", "Fraîcheur durable"], img: "https://images.unsplash.com/photo-1505693419163-5f5b40288cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "KING SIZE (180+)": { subtitle: "L'excellence santé", features: ["Soin exhaustif", "Traitement en profondeur", "Extraction maximale", "Stérilisation vapeur", "Protection textile"], img: "https://images.unsplash.com/photo-1540518614846-7eded433c457?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "CHAISES / FAUTEUILS": { subtitle: "Brillance & Hygiène", features: ["Nettoyage dossier & siège", "Détachage textile", "Désinfection vapeur", "Shampouinage rapide", "Résultat visible"], img: "https://images.unsplash.com/photo-1519947486511-46149fa0a254?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "TAPIS / MOQUETTES": { subtitle: "Soin des fibres", featured: true, features: ["Analyse du textile", "Méthode adaptée", "Nettoyage fibre à fibre", "Ravive les couleurs", "Protection longue durée"], img: "https://images.unsplash.com/photo-1534882197683-10e8d356c9a3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" },
    "INTERVENTIONS PRO": { subtitle: "Audit & Devis", features: ["Audit sur place", "Tarifs dégressifs", "Horaires flexibles", "Continuité d'activité", "Contrat d'entretien"], img: "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" }
};

const ReservationPage = () => {
    const location = useLocation();
    const [step, setStep] = useState(1);
    const [date, setDate] = useState(new Date());
    const [takenSlots, setTakenSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [planningConfig, setPlanningConfig] = useState([]); // List of days/hours
    const [blockedDates, setBlockedDates] = useState([]);      // List of ISO dates
    const [validationErrors, setValidationErrors] = useState([]); // Array of field names with issues
    const [toaster, setToaster] = useState({ show: false, message: '', type: 'error' });

    // Dynamic Pricing State
    const [prestationPrices, setPrestationPrices] = useState({}); // Map: "Title - Type" => Price
    const [addonsList, setAddonsList] = useState([]); // List of available options
    const [availablePrestations, setAvailablePrestations] = useState([]); // List for dropdown { label, value }
    const [prestationDurations, setPrestationDurations] = useState({}); // Map: "Title - Type" => Duration in min
    const [addonsDurations, setAddonsDurations] = useState({}); // Map: addonId => Duration in min
    const [userVehicles, setUserVehicles] = useState([]); // User's garage

    const [formData, setFormData] = useState({
        client_nom: '',
        client_email: '',
        client_telephone: '',
        prestation: '',
        password: '',
        passwordConfirm: ''
    });
    const [status, setStatus] = useState({ loading: false, success: false, error: null });
    const [user, setUser] = useState(pb.authStore.model);

    useEffect(() => {
        // Auto-fill if logged in
        if (pb.authStore.isValid && pb.authStore.model) {
            setUser(pb.authStore.model);
            setFormData(prev => ({
                ...prev,
                client_nom: pb.authStore.model.name || '',
                client_email: pb.authStore.model.email,
                client_telephone: pb.authStore.model.telephone || ''
            }));
        }

        // Listen to auth changes
        return pb.authStore.onChange((token, model) => {
            setUser(model);
            if (model) {
                setFormData(prev => ({
                    ...prev,
                    client_nom: model.name || '',
                    client_email: model.email,
                    client_telephone: model.telephone || ''
                }));
            }
        });
    }, []);

    // Fetch User's Garage
    useEffect(() => {
        const fetchGarage = async () => {
            if (user?.id) {
                try {
                    const records = await pb.collection('vehicules').getFullList({
                        filter: `user = "${user.id}"`,
                        sort: '-created'
                    });
                    setUserVehicles(records);
                } catch (err) {
                    console.error("Error fetching garage", err);
                }
            } else {
                setUserVehicles([]);
            }
        };
        fetchGarage();
    }, [user]);

    // Home Page Sync & Initial Data
    useEffect(() => {
        const savedAddress = localStorage.getItem('rp_diagnostic_address');
        const savedDistance = localStorage.getItem('rp_diagnostic_distance');
        const savedFee = localStorage.getItem('rp_diagnostic_fee');
        const savedCoords = localStorage.getItem('rp_diagnostic_coords');

        if (savedAddress && !address) {
            setAddress(savedAddress);
            if (savedDistance) setDistance(parseFloat(savedDistance));
            if (savedFee) setTravelFee(parseFloat(savedFee));
            if (savedCoords) setCoords(JSON.parse(savedCoords));
        }
    }, []);

    // Logistics State
    const [address, setAddress] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [distance, setDistance] = useState(null);
    const [travelFee, setTravelFee] = useState(0);
    const [isCalculating, setIsCalculating] = useState(false);
    const [coords, setCoords] = useState({ lat: null, lon: null });
    const [diagnosticPhoto, setDiagnosticPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Multi-Item State (Voiture / Mobilier)
    const [items, setItems] = useState(() => {
        // Handle incoming navigation state (e.g. from service pages)
        if (location.state && location.state.category) {
            const { category, mobilierCat, prestation, type, plaque } = location.state;
            return [{
                id: 1,
                category: category,
                type: type || (category === 'voiture' ? 'Citadine' : 'Mobilier'),
                prestation: prestation || '',
                addons: [],
                plaque: plaque || '',
                mobilierCat: mobilierCat || 'canapes'
            }];
        }
        return [{ id: 1, category: 'voiture', type: 'Citadine', prestation: '', addons: [], plaque: '', mobilierCat: 'canapes' }];
    });
    const [currentItemIndex, setCurrentItemIndex] = useState(0);

    // Old state kept for compatibility but not used directly
    // const [selectedAddons, setSelectedAddons] = useState([]); 

    // Upselling & Logic
    const [upsellOffer, setUpsellOffer] = useState(null);
    const [showUpsellModal, setShowUpsellModal] = useState(false);

    const checkUpsellOpportunity = () => {
        // Collect all currently selected addon IDs across all items
        const allSelectedAddons = items.flatMap(v => v.addons);

        // Check if "Cire" or "Protection" is already selected
        const hasWax = allSelectedAddons.some(id => {
            const addon = addonsList.find(a => a.id === id);
            return addon && (addon.label.toLowerCase().includes('cire') || addon.label.toLowerCase().includes('protection'));
        });

        if (hasWax) {
            return false; // Already has premium protection
        }

        // Candidates: Addons NOT already selected
        const candidates = addonsList.filter(a => !allSelectedAddons.includes(a.id));

        if (candidates.length === 0) return false; // No more options to sell

        // 1. Priority: Cire/Protection
        let bestOffer = candidates.find(a => a.label.toLowerCase().includes('cire')) ||
            candidates.find(a => a.label.toLowerCase().includes('protection'));

        // 2. Fallback: Take the first available cheap option (e.g., < 20€) or just the first one
        if (!bestOffer) {
            bestOffer = candidates.find(a => a.price < 30) || candidates[0];
        }

        if (bestOffer) {
            setUpsellOffer(bestOffer);
            setShowUpsellModal(true);
            return true;
        }

        return false;
    };

    const handleAcceptUpsell = () => {
        if (!upsellOffer) return;
        // Add to the first vehicle for simplicity, or current
        toggleAddonForItem(upsellOffer.id);
        setShowUpsellModal(false);
        setStep(5);
    };

    const handleDeclineUpsell = () => {
        setShowUpsellModal(false);
        setStep(5);
    };

    // Derived state for current item
    const currentItem = items[currentItemIndex];

    const SHOP_COORDS = { lat: 49.0067, lon: 1.9100 }; // Meulan-en-Yvelines (Consistent with Landing Page)

    useEffect(() => {
        const fillUserData = () => {
            const user = pb.authStore.model;
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    client_nom: user.name || '',
                    client_email: user.email || ''
                }));
            }
        };

        fillUserData();
        loadSystemConfig(); // Fetch planning, closures AND PRICING
        return pb.authStore.onChange(fillUserData);
    }, []);

    // Load System Config (Planning, Closures, Tarifs)
    const loadSystemConfig = async () => {
        try {
            // 1. Load Planning
            const planRecords = await pb.collection('planning').getFullList();
            setPlanningConfig(planRecords);

            // 2. Load Blocked Dates
            const blockRecords = await pb.collection('fermetures').getFullList();
            setBlockedDates(blockRecords.map(r => new Date(r.date).toLocaleDateString('fr-CA')));

            // 3. Load Tarifs & Options
            const rates = await pb.collection('tarifs').getFullList({
                sort: 'ordre',
                requestKey: null
            });

            const newPricesMap = {};
            const newDurationsMap = {};
            const newPrestationsOptions = [];
            const newAddons = [];
            const newAddonsDurations = {};

            rates.forEach(rate => {
                if (rate.categorie === 'Option') {
                    // It's an addon
                    newAddons.push({
                        id: rate.id,
                        label: rate.titre,
                        price: rate.prix_citadine || 0, // Use primary price field as standard
                        description: rate.description
                    });
                    newAddonsDurations[rate.id] = rate.duree || 15; // Default 15 min if not set
                } else {
                    // It's a main service
                    const isAuto = ['Formule', 'Detailing', 'Interieur', 'Exterieur', 'Complet'].includes(rate.categorie);
                    const baseDuration = rate.duree || 90; // Default 1h30

                    if (isAuto) {
                        if (rate.prix_citadine) {
                            const name = `${rate.titre} - Citadine`;
                            newPricesMap[name] = rate.prix_citadine;
                            newDurationsMap[name] = baseDuration;
                            newPrestationsOptions.push({ label: `${name} (${rate.prix_citadine}€)`, value: name });
                        }
                        if (rate.prix_berline) {
                            const name = `${rate.titre} - Berline`;
                            newPricesMap[name] = rate.prix_berline;
                            newDurationsMap[name] = Math.round(baseDuration * 1.2); // +20% for Berline
                            newPrestationsOptions.push({ label: `${name} (${rate.prix_berline}€)`, value: name });
                        }
                        if (rate.prix_suv) {
                            const name = `${rate.titre} - SUV / Monospace`;
                            newPricesMap[name] = rate.prix_suv;
                            newDurationsMap[name] = Math.round(baseDuration * 1.5); // +50% for SUV
                            newPrestationsOptions.push({ label: `${name} (${rate.prix_suv}€)`, value: name });
                        }
                    } else {
                        // Mobilier / Other
                        const name = rate.titre;
                        newPricesMap[name] = rate.prix_citadine || 0;
                        newDurationsMap[name] = baseDuration;
                        newPrestationsOptions.push({ label: `${name} (${rate.prix_citadine || 0}€)`, value: name });
                    }
                }
            });

            setPrestationPrices(newPricesMap);
            setPrestationDurations(newDurationsMap);
            setAddonsList(newAddons);
            setAddonsDurations(newAddonsDurations);
            setAvailablePrestations(newPrestationsOptions);

            // Set default prestation if empty for first item
            if (newPrestationsOptions.length > 0 && !items[0].prestation) {
                const defaultPres = newPrestationsOptions[0].value;
                updateItem(0, 'prestation', defaultPres);
            }

        } catch (err) { console.error("Config Loading error", err); }
    };

    const showToast = (message, type = 'error') => {
        setToaster({ show: true, message, type });
        setTimeout(() => setToaster({ show: false, message: '', type: 'error' }), 4000);
    };

    // Load available slots when date changes
    const handleNext = () => {
        setValidationErrors([]); // Reset errors
        if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(50);

        let errors = [];

        // Validation logic per step
        if (step === 1) {
            items.forEach((item, index) => {
                if (!item.prestation) errors.push(`prestation-${index}`);
                // Plaque is now optional - no validation needed here
            });
            if (errors.length > 0) {
                setValidationErrors(errors);
                return showToast("Veuillez sélectionner une prestation pour chaque véhicule.");
            }
        }

        if (step === 3) {
            if (!address || !distance) {
                setValidationErrors(['address']);
                return showToast('Veuillez sélectionner une adresse valide dans la liste.');
            }
        }

        if (step === 4) {
            if (!selectedTime) {
                setValidationErrors(['time']);
                return showToast('Veuillez choisir un horaire pour votre rendez-vous.');
            }
        }

        if (step === 4) {
            const hasUpsell = checkUpsellOpportunity();
            if (!hasUpsell) setStep(5);
        } else {
            setStep(step + 1);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    useEffect(() => {
        if (date) {
            fetchAvailability(date);
            setSelectedTime(null);
        }
    }, [date]);

    // --- Multi-Item Logic ---
    const formatPlaque = (val) => {
        // French SIV format: AA-123-AA
        const cleaned = val.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 7);
        let formatted = cleaned;
        if (cleaned.length > 2) {
            formatted = cleaned.slice(0, 2) + '-' + cleaned.slice(2);
        }
        if (cleaned.length > 5) {
            formatted = formatted.slice(0, 6) + '-' + formatted.slice(6);
        }
        return formatted;
    };

    const addItem = (category) => {
        if (items.length >= 5) return;

        // Find best default prestation for this category
        let defaultPres = '';
        if (availablePrestations.length > 0) {
            const filtered = availablePrestations.filter(p => {
                if (category === 'voiture') return p.value.includes(' - Citadine');
                return !p.value.includes(' - Citadine') && !p.value.includes(' - Berline') && !p.value.includes(' - SUV');
            });
            defaultPres = filtered.length > 0 ? filtered[0].value : availablePrestations[0].value;
        }

        setItems([...items, {
            id: Date.now(),
            category: category,
            type: category === 'voiture' ? 'Citadine' : 'Mobilier',
            prestation: defaultPres,
            addons: [],
            plaque: '',
            mobilierCat: 'canapes'
        }]);
        setCurrentItemIndex(items.length);
    };

    const removeItem = (index) => {
        if (items.length <= 1) return;
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        setCurrentItemIndex(Math.max(0, currentItemIndex - 1));
    };

    const updateItem = (index, field, value) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[index] = { ...newItems[index], [field]: value };
            return newItems;
        });
    };

    const updateItemFields = (index, fields) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[index] = { ...newItems[index], ...fields };
            return newItems;
        });
    };

    const toggleAddonForItem = (addonId) => {
        const currentAddons = items[currentItemIndex].addons;
        let newAddons;
        if (currentAddons.includes(addonId)) {
            newAddons = currentAddons.filter(id => id !== addonId);
        } else {
            newAddons = [...currentAddons, addonId];
        }
        updateItem(currentItemIndex, 'addons', newAddons);
    };

    // Format date for PB query "2023-01-01 00:00:00"
    const formatDateForQuery = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchAvailability = async (selectedDate) => {
        try {
            const dateString = formatDateForQuery(selectedDate);
            const startStr = `${dateString} 00:00:00`;
            const endStr = `${dateString} 23:59:59`;

            const records = await pb.collection('rendez_vous').getList(1, 100, {
                filter: `date_heure >= "${startStr}" && date_heure <= "${endStr}" && statut != "refuse"`,
                requestKey: null
            });

            const taken = records.items.map(item => {
                const d = new Date(item.date_heure);
                const startMin = d.getHours() * 60 + d.getMinutes();

                // Estimate duration for existing booking
                // Since we might have 'Multi-Prestations', we use a default if not found
                const duration = prestationDurations[item.prestation] || 90;

                return {
                    start: startMin,
                    end: startMin + duration,
                    timeStr: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                };
            });
            setTakenSlots(taken);
        } catch (err) {
            console.error("Error fetching slots", err);
        }
    };



    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of earth in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const handleAddressChange = async (e) => {
        const value = e.target.value;
        setAddress(value);
        setDistance(null); // Reset distance on change
        setTravelFee(0);
        setValidationErrors(prev => prev.filter(err => err !== 'address'));

        if (value.length > 3) {
            try {
                const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5`);
                const data = await response.json();
                setSuggestions(data.features || []);
                setShowSuggestions(true);
            } catch (err) {
                console.error("Address API error", err);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const selectAddress = (feature) => {
        const label = feature.properties.label;
        const [lon, lat] = feature.geometry.coordinates; // Verify order: API sends [lon, lat]

        setAddress(label);
        setSuggestions([]);
        setShowSuggestions(false);
        setIsCalculating(true);

        // Calculate Distance immediately
        const d = calculateDistance(SHOP_COORDS.lat, SHOP_COORDS.lon, lat, lon);
        setDistance(d);
        setCoords({ lat, lon });

        // Consistent Fee Logic with Landing Page
        let fee = 0;
        if (d > 20.5) {
            fee = (d - 20) * 0.70;
        }

        setTravelFee(parseFloat(fee.toFixed(2)));
        setIsCalculating(false);
        setValidationErrors(prev => prev.filter(err => err !== 'address'));
    };

    const handleTimeClick = (time) => {
        if (takenSlots.some(s => s.timeStr === time)) return;
        setSelectedTime(time);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const toggleAddon = (addonId) => {
        toggleAddonForItem(addonId);
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setDiagnosticPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setDiagnosticPhoto(null);
        setPhotoPreview(null);
    };

    const getPriceBreakdown = () => {
        let servicesTotal = 0;
        let optionsTotal = 0;

        items.forEach(v => {
            servicesTotal += prestationPrices[v.prestation] || 0;
            optionsTotal += v.addons.reduce((sum, id) => {
                const addon = addonsList.find(a => a.id === id);
                return sum + (addon ? addon.price : 0);
            }, 0);
        });

        let subtotal = servicesTotal + optionsTotal;
        let discount = 0;
        if (items.length === 2) discount = subtotal * 0.05;
        if (items.length >= 3) discount = subtotal * 0.10;

        const total = Math.round(subtotal - discount + travelFee);

        return {
            servicesTotal,
            optionsTotal,
            discount: Math.round(discount),
            total
        };
    };

    const calculateTotal = () => {
        return getPriceBreakdown().total;
    };

    const getDurationMinutes = () => {
        let totalMin = 0;
        items.forEach(v => {
            const baseDuration = prestationDurations[v.prestation] || 0;
            const addonsDuration = v.addons.reduce((sum, id) => {
                return sum + (addonsDurations[id] || 0);
            }, 0);
            totalMin += baseDuration + addonsDuration;
        });
        return totalMin;
    };

    const calculateTotalDuration = () => {
        const totalMin = getDurationMinutes();
        const hours = Math.floor(totalMin / 60);
        const mins = totalMin % 60;
        if (hours === 0) return `${mins} min`;
        if (mins === 0) return `${hours}h`;
        return `${hours}h${mins < 10 ? '0' : ''}${mins}`;
    };

    // Filter Slots based on Duration and Closing Time (19:00 max end)
    const getDynamicTimes = () => {
        const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
        // Capitalize first letter to match DB: 'lundi' -> 'Lundi'
        const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        const daySetting = planningConfig.find(p => p.jour === dayFormatted);

        // Default hours if nothing in DB
        let openHour = 9;
        let closeHour = 19;

        if (daySetting) {
            if (!daySetting.ouvert) return [];
            openHour = parseInt(daySetting.debut.split(':')[0]) || 9;
            closeHour = parseInt(daySetting.fin.split(':')[0]) || 19;
        }

        const durationMin = getDurationMinutes();
        const closingTimeLimit = closeHour * 60;

        return TIMES.filter(time => {
            const [h, m] = time.split(':').map(Number);
            const startMin = h * 60 + m;
            const endMin = startMin + durationMin;

            // 1. Outside opening hours?
            if (h < openHour || endMin > closingTimeLimit) return false;

            // 2. Collision with existing booking?
            const hasCollision = takenSlots.some(slot => {
                // If it starts during another booking OR ends during another booking
                // OR wraps around another booking
                return (startMin >= slot.start && startMin < slot.end) ||
                    (endMin > slot.start && endMin <= slot.end) ||
                    (startMin <= slot.start && endMin >= slot.end);
            });

            return !hasCollision;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTime) return alert("Veuillez choisir un horaire.");

        setStatus({ loading: true, success: false, error: null });
        try {
            let finalUser = user;

            // 1. Create account if not logged in
            if (!user) {
                if (formData.password !== formData.passwordConfirm) {
                    throw new Error("Les mots de passe ne correspondent pas.");
                }
                if (!formData.password || formData.password.length < 8) {
                    throw new Error("Le mot de passe doit faire au moins 8 caractères.");
                }

                const generateReferralCode = () => 'RP-' + Math.random().toString(36).substring(2, 6).toUpperCase();

                const userPayload = {
                    email: formData.client_email,
                    password: formData.password,
                    passwordConfirm: formData.passwordConfirm,
                    name: formData.client_nom,
                    telephone: formData.client_telephone,
                    emailVisibility: true,
                    referral_code: generateReferralCode(),
                    points_bonus: 0,
                    username: `user_${Math.random().toString(36).substr(2, 9)}`
                };

                const createdUser = await pb.collection('users').create(userPayload);
                await pb.collection('users').authWithPassword(formData.client_email, formData.password);
                finalUser = createdUser;
                setUser(pb.authStore.model);
            }
            // Construct full Date object for selected slot
            const [hours, minutes] = selectedTime.split(':');
            const appointmentDate = new Date(date);
            appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            // Construct Options String
            const optionsSummary = items.map((v, i) => {
                const addonsStr = v.addons.map(id => {
                    const opt = addonsList.find(a => a.id === id);
                    return opt ? opt.label : '';
                }).join(', ');
                const plaqueStr = v.category === 'voiture' ? ` [Plaque: ${v.plaque || '?'}]` : '';
                const itemTypeLabel = v.category === 'voiture' ? `Véhicule ${i + 1}` : `Mobilier ${i + 1}`;
                return `${itemTypeLabel}: ${v.prestation}${plaqueStr} [Options: ${addonsStr}]`;
            }).join(' | ');

            const breakdown = getPriceBreakdown();
            const dataObj = {
                ...formData,
                prestation: items.length > 1 ? 'Multi-Prestations' : items[0].prestation,
                date_heure: appointmentDate.toISOString(),
                statut: 'en_attente',
                adresse: address,
                lat: coords.lat,
                lon: coords.lon,
                prix: breakdown.total,
                frais_deplacement: travelFee,
                total_options: breakdown.optionsTotal,
                remise_montant: breakdown.discount,
                duree: getDurationMinutes(),
                options: optionsSummary,
                user: finalUser.id
            };

            // Use FormData for file upload
            const formPayload = new FormData();
            Object.keys(dataObj).forEach(key => {
                formPayload.append(key, dataObj[key]);
            });

            if (diagnosticPhoto) {
                formPayload.append('photos', diagnosticPhoto);
            }

            const record = await pb.collection('rendez_vous').create(formPayload);

            // 2. Save vehicles to Garage if needed
            for (const item of items) {
                if (item.category === 'voiture' && item.plaque) {
                    try {
                        const cleanPlaque = item.plaque.toUpperCase().replace(/\s/g, '');
                        const existingVehicles = await pb.collection('vehicules').getList(1, 1, {
                            filter: `user = "${finalUser.id}" && immatriculation = "${cleanPlaque}"`
                        });

                        if (existingVehicles.totalItems === 0) {
                            await pb.collection('vehicules').create({
                                user: finalUser.id,
                                immatriculation: cleanPlaque,
                                categorie: item.type.toLowerCase().includes('citadine') ? 'citadine' :
                                    item.type.toLowerCase().includes('berline') ? 'berline' : 'suv'
                            });
                        }
                    } catch (vehErr) {
                        console.error("Error saving vehicle to garage:", vehErr);
                    }
                }
            }

            // Trigger Notification
            notifyNewBooking(record);

            // Trigger Confetti Effect
            const duration = 5 * 1000;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

            const randomInRange = (min, max) => Math.random() * (max - min) + min;

            const interval = setInterval(function () {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    return clearInterval(interval);
                }

                const particleCount = 50 * (timeLeft / duration);
                // since particles fall down, start a bit higher than random
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }, 250);

            setStatus({ loading: false, success: true, error: null });
            fetchAvailability(date);
        } catch (err) {
            console.error(err);
            setStatus({ loading: false, success: false, error: err.message || "Erreur lors de la réservation." });
        }
    };

    return (
        <div className="reservation-page-wrapper">
            {/* PREMIUM TOAST */}
            <AnimatePresence>
                {toaster.show && (
                    <motion.div
                        className={`premium-toast ${toaster.type}`}
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -100, opacity: 0 }}
                    >
                        <div className="toast-icon">
                            {toaster.type === 'error' ? <FaTimes /> : <FaCheckCircle />}
                        </div>
                        <div className="toast-content">{toaster.message}</div>
                        <div className="toast-progress"></div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="container reservation-main-content">
                <header className="reservation-header-minimal">
                    <span className="res-label-premium">VOTRE RENDEZ-VOUS</span>
                    <h1 className="res-title-premium">RÉSERVATION</h1>
                    <div className="res-underline-premium"></div>
                </header>

                <div className="res-grid">
                    {/* Wizard Progress Bar */}
                    <div className="wizard-progress-bar">
                        {[
                            { id: 1, label: "Service", icon: <FaCar /> },
                            { id: 2, label: "Options", icon: <FaStar /> },
                            { id: 3, label: "Lieu", icon: <FaMapMarkerAlt /> },
                            { id: 4, label: "Horaire", icon: <FaCalendarAlt /> },
                            { id: 5, label: "Validation", icon: <FaCheckCircle /> }
                        ].map((s) => (
                            <div
                                key={s.id}
                                className={`progress-step ${step >= s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''} clickable`}
                                onClick={() => setStep(s.id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="step-icon">{step > s.id ? <FaCheckCircle /> : s.icon}</div>
                                <span>{s.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className="reservation-desktop-layout">
                        <div className="wizard-container">
                            <AnimatePresence mode="wait">
                                {step === 1 && (
                                    <motion.div
                                        key="step1"
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="wizard-step-content"
                                    >
                                        <div className="step-view">
                                            <div className="selector-header center">
                                                <span className="premium-label">ÉTAPE 1 : CHOISISSEZ VOTRE {currentItem.category === 'voiture' ? 'VÉHICULE' : 'MOBILIER'}</span>
                                                <h2 className="section-title">Quel est votre <span className="text-gradient-blue">{currentItem.category === 'voiture' ? 'modèle' : 'équipement'} ?</span></h2>
                                            </div>

                                            <div className="vehicle-tabs-premium">
                                                {items.map((v, index) => (
                                                    <div
                                                        key={v.id}
                                                        className={`vehicle-tab ${index === currentItemIndex ? 'active' : ''}`}
                                                        onClick={() => setCurrentItemIndex(index)}
                                                    >
                                                        <div className="vehicle-tab-icon">{v.category === 'voiture' ? <FaCar /> : <FaCouch />}</div>
                                                        <span>{v.category === 'voiture' ? 'Voiture' : 'Mobilier'} {index + 1}</span>
                                                        {items.length > 1 && (
                                                            <span
                                                                className="remove-vehicle-btn"
                                                                onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                                                            >
                                                                &times;
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                                {items.length < 5 && (
                                                    <div className="add-vehicle-tabs-group">
                                                        <div className="vehicle-tab add-tab" onClick={() => addItem('voiture')}>
                                                            <FaPlus /> Voiture
                                                        </div>
                                                        <div className="vehicle-tab add-tab" onClick={() => addItem('mobilier')}>
                                                            <FaPlus /> Mobilier
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <p className="step-subtitle">Configuration de : <strong>{currentItem.category === 'voiture' ? 'Véhicule' : 'Mobilier'} {currentItemIndex + 1}</strong></p>

                                            {currentItem.category === 'voiture' ? (
                                                <>
                                                    <div className="type-selector-grid reservation-type-grid">
                                                        {CATEGORIES.map((cat) => (
                                                            <button
                                                                key={cat.id}
                                                                className={`type-card ${currentItem.type === cat.id ? 'active' : ''}`}
                                                                onClick={() => updateItem(currentItemIndex, 'type', cat.id)}
                                                            >
                                                                <div className="type-icon">{cat.icon}</div>
                                                                <div className="type-info">
                                                                    <span className="type-name">{cat.name}</span>
                                                                    <span className="type-label">{cat.label}</span>
                                                                </div>
                                                                {currentItem.type === cat.id && <motion.div layoutId="active-glow-voiture" className="active-glow" />}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {userVehicles.length > 0 && (
                                                        <div className="garage-selector-premium">
                                                            <p className="garage-label"><FaShieldAlt /> SÉLECTIONNER DANS VOTRE GARAGE :</p>
                                                            <div className="garage-chips">
                                                                {userVehicles.map(veh => (
                                                                    <button
                                                                        key={veh.id}
                                                                        className={`garage-chip ${currentItem.plaque === veh.immatriculation ? 'active' : ''}`}
                                                                        onClick={() => {
                                                                            const formattedPlaque = formatPlaque(veh.immatriculation);
                                                                            const catMap = { 'citadine': 'Citadine', 'berline': 'Berline', 'suv': 'SUV / Monospace' };

                                                                            const updates = { plaque: formattedPlaque };
                                                                            if (catMap[veh.categorie]) {
                                                                                updates.type = catMap[veh.categorie];
                                                                            }

                                                                            updateItemFields(currentItemIndex, updates);
                                                                            setValidationErrors(prev => prev.filter(err => err !== `plaque-${currentItemIndex}`));
                                                                            showToast(`Véhicule ${formattedPlaque} sélectionné !`, 'success');
                                                                        }}
                                                                    >
                                                                        <FaCarSide />
                                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                                            <span>{veh.immatriculation}</span>
                                                                            <small style={{ fontSize: '0.65rem', opacity: 0.8 }}>{veh.categorie}</small>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="plaque-input-container">
                                                        <div className="plaque-label-row">
                                                            <label>Plaque d'immatriculation <span className="optional-tag">(Optionnel)</span></label>
                                                            <div className="plaque-tooltip-trigger">
                                                                <FaInfoCircle />
                                                                <div className="plaque-tooltip">
                                                                    Utile pour préparer l'intervention, mais vous pouvez la fournir plus tard.
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={`plaque-input-wrapper ${validationErrors.includes(`plaque-${currentItemIndex}`) ? 'input-error-shake' : ''}`}>
                                                            <div className="plaque-eu-strip"></div>
                                                            <input
                                                                type="text"
                                                                placeholder="AB-123-CD"
                                                                value={currentItem.plaque}
                                                                onChange={(e) => {
                                                                    updateItem(currentItemIndex, 'plaque', formatPlaque(e.target.value));
                                                                    setValidationErrors(prev => prev.filter(err => err !== `plaque-${currentItemIndex}`));
                                                                }}
                                                                className="plaque-field"
                                                                maxLength="9"
                                                            />
                                                            <div className="plaque-dept-strip"></div>
                                                        </div>
                                                        <p className="plaque-help-hint">Je ne connais pas ma plaque ? Laissez ce champ vide.</p>
                                                    </div>

                                                    <h4 className="prestation-section-title">Choisissez votre forfait pour {currentItem.type}</h4>

                                                    <div className="pricing-grid-premium reservation-pricing-grid">
                                                        {availablePrestations
                                                            .filter(p => p.value.includes(` - ${currentItem.type.split(' / ')[0]}`))
                                                            .map((p) => {
                                                                const formulaName = p.value.split(' - ')[0];
                                                                const details = FORMULA_DETAILS[formulaName] || {
                                                                    subtitle: "Service Professionnel",
                                                                    features: ["Nettoyage complet", "Expertise RoulezPropre"],
                                                                    img: luxe1
                                                                };

                                                                return (
                                                                    <motion.div
                                                                        key={p.value}
                                                                        className={`pricing-card-premium ${details.featured ? 'featured' : ''} ${currentItem.prestation === p.value ? 'selected-item' : ''}`}
                                                                        onClick={() => updateItem(currentItemIndex, 'prestation', p.value)}
                                                                    >
                                                                        <div className="pricing-card-img-wrapper">
                                                                            <img src={details.img} alt={formulaName} />
                                                                            <div className="card-badge">{formulaName}</div>
                                                                            <div className="card-overlay"></div>
                                                                        </div>
                                                                        <div className="pricing-card-content">
                                                                            <div className="category-tag">{details.subtitle}</div>
                                                                            <ul className="formula-features">
                                                                                {details.features.map((feat, i) => (
                                                                                    <li key={i}><FaCheckCircle className="check-icon" /> {feat}</li>
                                                                                ))}
                                                                            </ul>
                                                                            <div className="pricing-footer">
                                                                                <div className="price-info">
                                                                                    <span className="price-from">dès</span>
                                                                                    <span className="price-value">{p.label.match(/\(([^)]+)\)/)?.[1] || '0€'}</span>
                                                                                </div>
                                                                                <div className={`btn-premium-small ${currentItem.prestation === p.value ? 'active-btn' : ''}`}>
                                                                                    {currentItem.prestation === p.value ? 'SÉLECTIONNÉ' : 'CHOISIR'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        {currentItem.prestation === p.value && <div className="selection-border"></div>}
                                                                    </motion.div>
                                                                );
                                                            })}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="type-selector-grid reservation-type-grid">
                                                        {MOBILIER_CATEGORIES.map((cat) => (
                                                            <button
                                                                key={cat.id}
                                                                className={`type-card ${currentItem.mobilierCat === cat.id ? 'active' : ''}`}
                                                                onClick={() => updateItem(currentItemIndex, 'mobilierCat', cat.id)}
                                                            >
                                                                <div className="type-icon">{cat.icon}</div>
                                                                <div className="type-info">
                                                                    <span className="type-name">{cat.name}</span>
                                                                    <span className="type-label">{cat.label}</span>
                                                                </div>
                                                                {currentItem.mobilierCat === cat.id && <motion.div layoutId="active-glow-mob" className="active-glow" />}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    <h4 className="prestation-section-title">Sélectionnez la prestation mobilier</h4>
                                                    <div className="pricing-grid-premium reservation-pricing-grid">
                                                        {availablePrestations
                                                            .filter(p => {
                                                                const isAuto = p.value.includes(' - Citadine') || p.value.includes(' - Berline') || p.value.includes(' - SUV');
                                                                if (isAuto) return false;

                                                                const name = p.value.toUpperCase();
                                                                if (currentItem.mobilierCat === 'canapes') return name.includes('CANAPÉ');
                                                                if (currentItem.mobilierCat === 'matelas') return name.includes('PLACE') || name.includes('KING');
                                                                if (currentItem.mobilierCat === 'autres') return name.includes('CHAISE') || name.includes('TAPIS') || name.includes('PRO');
                                                                return true;
                                                            })
                                                            .map((p) => {
                                                                const formulaName = p.value;
                                                                const details = MOBILIER_DETAILS[formulaName] || {
                                                                    subtitle: "Service Mobilier",
                                                                    features: ["Nettoyage en profondeur", "Traitement textile"],
                                                                    img: "https://images.unsplash.com/photo-1550254478-ead40cc54513?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                                                };

                                                                return (
                                                                    <motion.div
                                                                        key={p.value}
                                                                        className={`pricing-card-premium ${details.featured ? 'featured' : ''} ${currentItem.prestation === p.value ? 'selected-item' : ''}`}
                                                                        onClick={() => updateItem(currentItemIndex, 'prestation', p.value)}
                                                                    >
                                                                        <div className="pricing-card-img-wrapper">
                                                                            <img src={details.img} alt={formulaName} />
                                                                            <div className="card-badge">{formulaName}</div>
                                                                            <div className="card-overlay"></div>
                                                                        </div>
                                                                        <div className="pricing-card-content">
                                                                            <div className="category-tag">{details.subtitle}</div>
                                                                            <ul className="formula-features">
                                                                                {details.features.map((feat, i) => (
                                                                                    <li key={i}><FaCheckCircle className="check-icon" /> {feat}</li>
                                                                                ))}
                                                                            </ul>
                                                                            <div className="pricing-footer">
                                                                                <div className="price-info">
                                                                                    <span className="price-from">dès</span>
                                                                                    <span className="price-value">{p.label.match(/\(([^)]+)\)/)?.[1] || '0€'}</span>
                                                                                </div>
                                                                                <div className={`btn-premium-small ${currentItem.prestation === p.value ? 'active-btn' : ''}`}>
                                                                                    {currentItem.prestation === p.value ? 'SÉLECTIONNÉ' : 'CHOISIR'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        {currentItem.prestation === p.value && <div className="selection-border"></div>}
                                                                    </motion.div>
                                                                );
                                                            })}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                )}

                                {step === 2 && (
                                    <motion.div
                                        key="step2"
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="wizard-step-content"
                                    >
                                        <div className="step-view">
                                            <h3>Optimisez votre nettoyage</h3>
                                            <div className="vehicle-tabs-premium">
                                                {items.map((v, index) => (
                                                    <div
                                                        key={v.id}
                                                        className={`vehicle-tab ${index === currentItemIndex ? 'active' : ''}`}
                                                        onClick={() => setCurrentItemIndex(index)}
                                                    >
                                                        <div className="vehicle-tab-icon">{v.category === 'voiture' ? <FaCar /> : <FaCouch />}</div>
                                                        <span>{index + 1}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <p className="step-subtitle">Options pour : <strong>{currentItem.category === 'voiture' ? 'Véhicule' : 'Mobilier'} {currentItemIndex + 1}</strong></p>

                                            <div className="addons-grid-premium">
                                                {addonsList.map(addon => (
                                                    <div
                                                        key={addon.id}
                                                        className={`addon-card-premium ${currentItem.addons.includes(addon.id) ? 'selected' : ''}`}
                                                        onClick={() => toggleAddonForItem(addon.id)}
                                                    >
                                                        <div className="addon-meta">
                                                            <span className="addon-name">{addon.label}</span>
                                                            {addon.description && <span className="addon-desc">{addon.description}</span>}
                                                            <span className="addon-price">+{addon.price}€</span>
                                                        </div>
                                                        <div className="addon-status">{currentItem.addons.includes(addon.id) ? 'Inclus' : 'Ajouter'}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="diagnostic-upload-premium">
                                                <div className="upload-header">
                                                    <FaCamera />
                                                    <h4>Diagnostic visuel <span className="facultatif">(facultatif)</span></h4>
                                                </div>
                                                <p>Ajoutez une photo pour nous aider à mieux préparer l'intervention</p>

                                                <div className={`upload-zone ${photoPreview ? 'has-file' : ''}`}>
                                                    {!photoPreview ? (
                                                        <label className="upload-label">
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handlePhotoChange}
                                                                style={{ display: 'none' }}
                                                            />
                                                            <span>Cliquez pour choisir une photo</span>
                                                        </label>
                                                    ) : (
                                                        <div className="photo-preview-container">
                                                            <img src={photoPreview} alt="Diagnostic preview" />
                                                            <button className="remove-photo-btn" onClick={removePhoto}>&times;</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {step === 3 && (
                                    <motion.div
                                        key="step3"
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="wizard-step-content"
                                    >
                                        <div className="step-view">
                                            <div className="diagnostic-step-wrapper">
                                                <div className="diagnostic-header-premium">
                                                    <span className="diag-badge">Diagnostic Instantané</span>
                                                    <h3 className="diag-title-premium">On vient à <span className="text-highlight-blue">votre rencontre</span></h3>
                                                    <p className="diag-subtitle-premium">Roulez Propre se déplace directement chez vous ou sur votre lieu de travail.</p>
                                                </div>

                                                <div className="address-box-premium">
                                                    <div className={`input-with-icon ${validationErrors.includes('address') ? 'input-error-shake' : ''}`} style={{ position: 'relative' }}>
                                                        <FaSearch />
                                                        <input
                                                            required
                                                            type="text"
                                                            placeholder="Saisissez votre ville ou adresse exacte..."
                                                            value={address}
                                                            onChange={handleAddressChange}
                                                            autoComplete="off"
                                                        />
                                                        {showSuggestions && suggestions.length > 0 && (
                                                            <div className="suggestions-dropdown-premium">
                                                                {suggestions.map((feature) => (
                                                                    <div
                                                                        key={feature.properties.id}
                                                                        className="suggestion-item"
                                                                        onClick={() => selectAddress(feature)}
                                                                    >
                                                                        <FaMapMarkerAlt style={{ color: '#3b82f6' }} />
                                                                        <strong>{feature.properties.label}</strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <AnimatePresence>
                                                        {distance !== null && !isCalculating && (
                                                            <motion.div
                                                                className="diagnostic-result-card-premium"
                                                                initial={{ opacity: 0, scale: 0.95 }}
                                                                animate={{ opacity: 1, scale: 1 }}
                                                            >
                                                                <div className="diag-result-icon-box">
                                                                    {travelFee === 0 ? <FaCheckCircle /> : <FaMoneyBillWave />}
                                                                </div>
                                                                <div className="diag-result-text">
                                                                    <h4>{travelFee === 0 ? "Déplacement OFFERT !" : "Service disponible"}</h4>
                                                                    <p>{travelFee === 0 ? `Bonne nouvelle ! Nous intervenons sans frais à ${distance.toFixed(1)} km.` : `Nous pouvons intervenir chez vous (${distance.toFixed(1)} km).`}</p>
                                                                </div>
                                                                <div className="diag-price-hero">
                                                                    <span className="diag-price-value">{travelFee}€</span>
                                                                    <span className="diag-price-label">Frais de trajet</span>
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {step === 4 && (
                                    <motion.div
                                        key="step4"
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="wizard-step-content"
                                    >
                                        <div className="step-view">
                                            <h3>Choisissez votre créneau</h3>
                                            <div className="calendar-time-grid">
                                                <div className="calendar-col">
                                                    <Calendar
                                                        onChange={setDate}
                                                        value={date}
                                                        minDate={new Date()}
                                                        tileDisabled={({ date }) => {
                                                            const dJson = date.toLocaleDateString('fr-CA');
                                                            const isBlocked = blockedDates.includes(dJson);

                                                            // Check planning (Weekly closures)
                                                            const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
                                                            const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
                                                            const daySetting = planningConfig.find(p => p.jour === dayFormatted);
                                                            const isClosedWeekly = daySetting && !daySetting.ouvert;

                                                            return date < new Date().setHours(0, 0, 0, 0) || isBlocked || isClosedWeekly;
                                                        }}
                                                        tileClassName={({ date }) => {
                                                            const dJson = date.toLocaleDateString('fr-CA');
                                                            const isBlocked = blockedDates.includes(dJson);

                                                            // Check planning (Weekly closures) - Using French day names
                                                            const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
                                                            const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
                                                            const daySetting = planningConfig.find(p => p.jour === dayFormatted);
                                                            const isClosedWeekly = daySetting && !daySetting.ouvert;
                                                            const isPast = date < new Date().setHours(0, 0, 0, 0);

                                                            let classes = [];
                                                            if (isBlocked || isClosedWeekly) classes.push('react-calendar__tile--blocked');
                                                            if (isPast) classes.push('react-calendar__tile--past');

                                                            return classes.join(' ');
                                                        }}
                                                        className="custom-calendar-premium"
                                                    />
                                                </div>
                                                <div className="slots-col">
                                                    <h4>Horaires pour le {date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</h4>
                                                    <div className="slots-grid-premium">
                                                        {getDynamicTimes().map(time => (
                                                            <button
                                                                key={time}
                                                                className={`slot-pill ${takenSlots.some(s => s.timeStr === time) ? 'taken' : ''} ${selectedTime === time ? 'selected' : ''}`}
                                                                onClick={() => handleTimeClick(time)}
                                                                disabled={takenSlots.some(s => s.timeStr === time)}
                                                            >
                                                                <FaClock /> {time}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {getDynamicTimes().length === 0 ? (
                                                        <div className="no-slots-alert">
                                                            ðŸš« {planningConfig.find(p => p.jour === date.toLocaleDateString('en-US', { weekday: 'long' }))?.ouvert === false ? "Fermé le " + date.toLocaleDateString('fr-FR', { weekday: 'long' }) : "Plus de créneaux disponibles pour cette durée."}
                                                        </div>
                                                    ) : (
                                                        <div className="reservation-quick-summary-bar">
                                                            <div className="summary-stat">
                                                                <span className="stat-label">Total Estimé</span>
                                                                <span className="stat-value">{calculateTotal()}€</span>
                                                            </div>
                                                            <div className="summary-stat">
                                                                <span className="stat-label">Durée Estimée</span>
                                                                <span className="stat-value">{calculateTotalDuration()}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {showUpsellModal && upsellOffer && (
                                            <div className="upsell-modal-overlay">
                                                <div className="upsell-modal-card">
                                                    <div className="upsell-badge">OFFRE SPÉCIALE</div>
                                                    <h3>Pour une finition parfaite...</h3>
                                                    <p>Ajoutez l'option <strong>{upsellOffer.label}</strong> pour seulement {upsellOffer.price}€.</p>
                                                    <div className="upsell-actions">
                                                        <button className="btn-upsell-accept" onClick={handleAcceptUpsell}><FaCheckCircle /> J'ajoute</button>
                                                        <button className="btn-upsell-decline" onClick={handleDeclineUpsell}>Non merci</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}

                                {step === 5 && (
                                    <motion.div
                                        key="step5"
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="wizard-step-content"
                                    >
                                        <div className="step-view">
                                            <div className="final-step-grid">
                                                <div className="confirmation-details">
                                                    <h3>Dernière étape</h3>
                                                    {status.success ? (
                                                        <div className="success-wizard-card">
                                                            <FaCheckCircle className="success-icon" />
                                                            <h4>Résumé envoyé !</h4>
                                                            <p>Nous avons bien reçu votre demande pour le {date.toLocaleDateString()} à {selectedTime}.</p>
                                                            <button className="btn-finish" onClick={() => window.location.href = '/'}>Retourner à l'accueil</button>
                                                        </div>
                                                    ) : (
                                                        <div className="final-form-container">
                                                            {!user ? (
                                                                <div className="auth-prompt-premium">
                                                                    <div className="auth-prompt-icon"><FaUser /></div>
                                                                    <div className="auth-prompt-text">
                                                                        <p>Déjà client ? <span className="login-link" onClick={() => document.dispatchEvent(new CustomEvent('openLoginModal'))}>Connectez-vous</span></p>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="auth-welcome-premium">
                                                                    <FaCheckCircle />
                                                                    <p>Connecté en tant que <strong>{user.name}</strong></p>
                                                                </div>
                                                            )}

                                                            <form onSubmit={handleSubmit} className="final-form-registration-integrated" id="final-form-id">
                                                                <div className="input-group-premium">
                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                                                        <div className="auth-field-wrapper">
                                                                            <label style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginLeft: '5px' }}>Nom complet</label>
                                                                            <input required type="text" name="client_nom" placeholder="Ex: Jean Dupont" value={formData.client_nom} onChange={handleFormChange} readOnly={!!user} className={user ? 'input-readonly' : ''} />
                                                                        </div>
                                                                        <div className="auth-field-wrapper">
                                                                            <label style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginLeft: '5px' }}>Téléphone</label>
                                                                            <input required type="tel" name="client_telephone" placeholder="06.." value={formData.client_telephone} onChange={handleFormChange} readOnly={!!user && !!user.telephone} className={user && user.telephone ? 'input-readonly' : ''} />
                                                                        </div>
                                                                    </div>

                                                                    <div className="auth-field-wrapper" style={{ marginBottom: '15px' }}>
                                                                        <label style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginLeft: '5px' }}>Adresse Email</label>
                                                                        <input required type="email" name="client_email" placeholder="email@exemple.com" value={formData.client_email} onChange={handleFormChange} readOnly={!!user} className={user ? 'input-readonly' : ''} />
                                                                    </div>

                                                                    {!user && (
                                                                        <div className="password-creation-zone" style={{ marginTop: '20px', padding: '15px', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '15px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                                                                            <p style={{ fontSize: '0.8rem', fontWeight: '800', color: 'var(--primary-blue)', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>ðŸ” SÉCURISATION DU COMPTE :</p>
                                                                            <div className="form-row-auth" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                                                                <div className="auth-field-wrapper">
                                                                                    <label style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-tertiary)', marginLeft: '5px' }}>Mot de passe</label>
                                                                                    <input required type="password" name="password" placeholder="8 car. min" value={formData.password} onChange={handleFormChange} minLength={8} />
                                                                                </div>
                                                                                <div className="auth-field-wrapper">
                                                                                    <label style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-tertiary)', marginLeft: '5px' }}>Confirmation</label>
                                                                                    <input required type="password" name="passwordConfirm" placeholder="Répéter" value={formData.passwordConfirm} onChange={handleFormChange} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <button type="submit" className="btn-confirm-final" disabled={status.loading}>
                                                                    {status.loading ? 'Traitement...' : 'CONFIRMER MA RÉSERVATION'}
                                                                </button>
                                                                {status.error && <p className="error-wizard">{status.error}</p>}
                                                            </form>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="order-summary-card-premium">
                                                    <h4><FaCheckCircle className="summary-icon" /> Récapitulatif</h4>

                                                    <div className="summary-info-header" style={{ marginBottom: '15px' }}>
                                                        <div className="summary-info-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                                                            <FaCalendarAlt /> <span>Le {date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à <strong>{selectedTime}</strong></span>
                                                        </div>
                                                        <div className="summary-info-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                            <FaMapMarkerAlt /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</span>
                                                        </div>
                                                    </div>

                                                    <div className="summary-divider" style={{ margin: '10px 0', height: '1px', background: 'var(--border-color)', opacity: '0.5' }}></div>

                                                    <div className="summary-section-detailed">
                                                        {items.map((v, i) => (
                                                            <div key={i} className="vehicle-summary-block" style={{ marginBottom: '15px' }}>
                                                                <div className="summary-item-main" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                    <div className="item-title" style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <strong style={{ fontSize: '0.9rem' }}>{v.category === 'voiture' ? 'Véhicule' : 'Mobilier'} {i + 1}</strong>
                                                                        <span className="item-prestation" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{v.prestation}</span>
                                                                    </div>
                                                                    <span className="item-price" style={{ fontWeight: '700' }}>{prestationPrices[v.prestation] || 0}€</span>
                                                                </div>

                                                                {v.addons && v.addons.length > 0 && (
                                                                    <div className="summary-item-addons" style={{ marginTop: '5px', paddingLeft: '10px', borderLeft: '2px solid var(--primary-blue)', marginLeft: '5px' }}>
                                                                        {v.addons.map(addonId => {
                                                                            const addon = addonsList.find(a => a.id === addonId);
                                                                            if (!addon) return null;
                                                                            return (
                                                                                <div key={addonId} className="addon-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '2px' }}>
                                                                                    <span className="addon-name">+ {addon.label}</span>
                                                                                    <span className="addon-price">{addon.price}€</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="summary-divider" style={{ margin: '15px 0' }}></div>

                                                    <div className="summary-footer-detailed" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {travelFee > 0 && (
                                                            <div className="summary-footer-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                                                <span>Frais de déplacement</span>
                                                                <span>{travelFee}€</span>
                                                            </div>
                                                        )}
                                                        {getPriceBreakdown().discount > 0 && (
                                                            <div className="summary-footer-line discount" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#10b981', fontWeight: '600' }}>
                                                                <span>Remise multi-véhicules</span>
                                                                <span>-{getPriceBreakdown().discount}€</span>
                                                            </div>
                                                        )}
                                                        <div className="summary-total-main" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px', paddingTop: '10px', borderTop: '2px dashed var(--border-color)' }}>
                                                            <span style={{ fontWeight: '800', textTransform: 'uppercase', fontSize: '0.9rem' }}>Total à régler</span>
                                                            <strong style={{ fontSize: '1.4rem', color: 'var(--primary-blue)' }}>{getPriceBreakdown().total}€</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {!status.success && (
                                                <div className="wizard-actions-final">
                                                    <button className="btn-back-premium" onClick={() => setStep(4)}><FaChevronLeft /> Retour</button>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {!status.success && step < 5 && (
                                <div className="horizontal-summary-bar">
                                    <div className="summary-stats">
                                        <div className="summary-stat">
                                            <span className="stat-label">Total Estimé</span>
                                            <span className="stat-value">{calculateTotal()}€</span>
                                        </div>
                                        <div className="summary-stat">
                                            <span className="stat-label">Durée Estimée</span>
                                            <span className="stat-value"><FaClock /> {calculateTotalDuration()}</span>
                                        </div>
                                    </div>
                                    <div className="summary-actions">
                                        {step > 1 && (
                                            <button className="btn-back-premium" onClick={() => setStep(step - 1)}>
                                                <FaChevronLeft /> Retour
                                            </button>
                                        )}
                                        <button
                                            className="btn-next-premium"
                                            onClick={handleNext}
                                        >
                                            {step === 4 ? 'Récapitulatif' : 'Suivant'} <FaChevronRight />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Bottom Sheet Summary */}
            {!status.success && (
                <div className="mobile-bottom-sheet">
                    <div className="sheet-content">
                        <div className="sheet-info">
                            <span className="sheet-total">{calculateTotal()}€</span>
                            <span className="sheet-duration"><FaClock /> {calculateTotalDuration()}</span>
                        </div>
                        {step < 5 ? (
                            <button className="btn-sheet-next" onClick={handleNext}>
                                Suivant <FaChevronRight />
                            </button>
                        ) : (
                            <button className="btn-sheet-next" form="final-form-id" onClick={() => document.querySelector('.btn-confirm-final').click()}>
                                Confirmer
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationPage;


